import { FormEvent, useEffect, useState } from 'react';
import type { MemberCreated, MemberCreateRequest, OutletRef } from '@kassa/shared';
import { api, ApiError } from '../../api';

// Yeni işçi formu (KT-41). Ayrı fayl: Pins ekranı ilə konflikt az olsun.
// E-poçt yazılıbsa — giriş dəvəti (KT-114), yoxdursa müvəqqəti parol cavabda gəlir; onu bu komponent saxlamır — valideyn bir dəfə göstərir.
const ROLES: [string, string][] = [
  ['manager', 'Menecer'],
  ['cashier', 'Kassir'],
  ['storekeeper', 'Anbardar'],
  ['accountant', 'Mühasib'],
  ['sales_rep', 'Satış təmsilçisi'],
];

// Login təklifi: ad.firma (Azərbaycan hərfləri latına). Sahibkar dəyişə bilər.
export function loginSuggestion(name: string, firm: string): string {
  const map: Record<string, string> = { ə: 'e', Ə: 'e', ş: 's', Ş: 's', ç: 'c', Ç: 'c', ğ: 'g', Ğ: 'g', ı: 'i', I: 'i', ö: 'o', Ö: 'o', ü: 'u', Ü: 'u', İ: 'i' };
  const clean = (v: string) =>
    v
      .replace(/[əƏşŞçÇğĞıIöÖüÜİ]/g, (c) => map[c] ?? c)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '')
      .slice(0, 20);
  const base = clean(name.trim().split(/\s+/)[0] ?? '');
  const f = clean(firm);
  return base && f ? `${base}.${f}`.slice(0, 40) : base || f;
}

interface Props {
  firmName: string;
  /** Əvvəlcədən seçilmiş rollar (məs. mühasib dəvətində hesab yoxdursa). */
  presetRoles?: string[];
  onCreated: (result: MemberCreated, name: string) => void;
  onCancel: () => void;
}

export function MemberForm({ firmName, presetRoles, onCreated, onCancel }: Props) {
  const [name, setName] = useState('');
  const [login, setLogin] = useState('');
  const [loginTouched, setLoginTouched] = useState(false);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [roles, setRoles] = useState<string[]>(presetRoles ?? []);
  const [outlets, setOutlets] = useState<OutletRef[]>([]);
  const [outletIds, setOutletIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.outlets().then(setOutlets).catch(() => setOutlets([]));
  }, []);

  // Login adı yazılmayıbsa və ya əl ilə dəyişilməyibsə, addan təklif olunur
  useEffect(() => {
    if (!loginTouched) setLogin(loginSuggestion(name, firmName));
  }, [name, firmName, loginTouched]);

  const toggle = (list: string[], v: string, set: (x: string[]) => void) =>
    set(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const identifierOk = login.trim() !== '' || phone.trim() !== '' || email.trim() !== '';
  const canSubmit = name.trim() !== '' && roles.length > 0 && identifierOk && !busy;

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    const body: MemberCreateRequest = {
      name: name.trim(),
      roles,
      login: login.trim() || undefined,
      phone: phone.trim() || undefined,
      email: email.trim() || undefined,
      outletIds: outletIds.length ? outletIds : undefined,
    };
    try {
      const result = await api.memberCreate(body);
      onCreated(result, name.trim());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Yadda saxlanılmadı. Yenidən cəhd edin.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="product-form" onSubmit={submit} noValidate aria-label="Yeni işçi">
      <h3>Yeni işçi</h3>
      <label className="field">
        Ad, soyad
        <input value={name} onChange={(e) => setName(e.target.value)} required disabled={busy} />
      </label>
      <label className="field">
        Login
        <input
          value={login}
          onChange={(e) => {
            setLoginTouched(true);
            setLogin(e.target.value.toLowerCase());
          }}
          autoComplete="off"
          disabled={busy}
        />
        <span className="muted small">İşçi bu adla girir (kiçik hərf, nöqtə, tire, alt xətt). Boş qoysanız, telefon və ya e-poçtla girir.</span>
      </label>
      <label className="field">
        Telefon (istəyə görə)
        <input inputMode="tel" placeholder="+994 50 000 00 00" value={phone} onChange={(e) => setPhone(e.target.value)} disabled={busy} />
      </label>
      <label className="field">
        E-poçt (istəyə görə)
        <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} disabled={busy} />
      </label>
      <fieldset className="choice-list">
        <legend>Rollar</legend>
        {ROLES.map(([code, label]) => (
          <label key={code} className="check">
            <input type="checkbox" checked={roles.includes(code)} onChange={() => toggle(roles, code, setRoles)} disabled={busy} /> {label}
          </label>
        ))}
      </fieldset>
      {outlets.length > 0 && (
        <fieldset className="choice-list">
          <legend>Obyektlər (boş — hamısı)</legend>
          {outlets.map((o) => (
            <label key={o.id} className="check">
              <input type="checkbox" checked={outletIds.includes(o.id)} onChange={() => toggle(outletIds, o.id, setOutletIds)} disabled={busy} /> {o.name}
            </label>
          ))}
        </fieldset>
      )}
      <p className="muted small">
        {email.trim()
          ? 'E-poçta giriş dəvəti gedəcək (72 saat): işçi linklə özü parol qoyacaq. Parol heç kimə göstərilmir.'
          : 'Müvəqqəti parol yaradılacaq və yalnız bir dəfə göstəriləcək. İşçi ilk girişdə yeni parol qoyacaq.'}
      </p>
      {error && (
        <p className="bad" role="alert">
          {error}
        </p>
      )}
      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={!canSubmit}>
          {busy ? 'Yaradılır…' : 'Yarat'}
        </button>
        <button type="button" className="link-btn" onClick={onCancel} disabled={busy}>
          Ləğv et
        </button>
      </div>
    </form>
  );
}

